import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { AppBar, ListItem, ListItemText, Toolbar, makeStyles } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';

import axiosInterceptor from 'src/app/axiosInterceptor';
import Logo from 'src/app/components/Logo';
import { MENU } from 'src/app/constant/menu';
import { _convertObjectToQuery } from 'src/app/utils/apiService';
import { logOutAction } from 'src/features/authSlice';
import { resetListVehicle } from 'src/features/vehicleSlice';
import AlertTransmissionModal from './AlertTransmissionModal';
import { itemsReport } from './constance';
import TopBarDesktopNavigation from './TopBarDesktopNavigation';
import TopBarMobileDrawer from './TopBarMobileDrawer';
import TopBarUserActions from './TopBarUserActions';
import './style.css';

const APP_ROOT = '/app';
const HOVER_MENU_CLOSE_DELAY = 400;
const DEVICE_MANAGE_PATHS = [
  '/manage/devices',
  '/manage/service-packages',
  '/manage/vehicle-types',
  '/manage/versions',
  '/manage/sim'
];

const useStyles = makeStyles(theme => ({
  root: {
    background: '#fff',
    boxShadow: '0 0 5px 0px #b3b3b3b3',
    height: '66px',
    '&.MuiPaper-elevation0': {
      boxShadow: 'none'
    }
  },
  menuHeader: {
    display: 'flex',
    justifyContent: 'center'
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    listStyleType: 'none',
    margin: 0,
    padding: 0
  },
  wrapLogo: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '16px'
  },
  desktopMenuButton: {
    minWidth: 'auto',
    marginLeft: '15px',
    padding: '20px',
    borderRadius: 0,
    color: '#475461',
    fontSize: '16px',
    fontWeight: 400,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      color: '#D9042F'
    }
  },
  desktopMenuButtonActive: {
    color: '#D9042F',
    fontWeight: 700
  },
  desktopMenuIcon: {
    marginLeft: theme.spacing(0.5),
    transition: 'transform 0.2s ease'
  },
  desktopMenuIconOpen: {
    transform: 'rotate(180deg)'
  },
  itemDropdownMenu: {
    color: '#475461',
    fontSize: '15px',
    backgroundColor: '#FFFFFF',
    transition: 'none',
    '&:hover': {
      color: '#D9042F',
      backgroundColor: theme.palette.background.light
    }
  },
  dropdownDivider: {
    margin: '0 10px',
    backgroundColor: '#D4DCE6'
  },
  desktopDropdownPaper: {
    minWidth: 220,
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 8,
    marginTop: -4,
    overflow: 'hidden',
    boxShadow: '0 18px 40px rgba(15, 23, 42, 0.14)',
    animation: '$fadeInDown 140ms ease-out'
  },
  desktopSubmenuPaper: {
    minWidth: 220,
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 18px 40px rgba(15, 23, 42, 0.16)',
    animation: '$fadeInRight 150ms ease-out'
  },
  alertBadge: {
    '& .MuiBadge-badge': {
      minWidth: 18,
      height: 18,
      padding: '0 5px',
      borderRadius: 9,
      backgroundColor: '#d9042f',
      color: '#fff',
      fontSize: 11,
      fontWeight: 700
    },
    '& .MuiBadge-anchorOriginTopRightRectangle': {
      transform: 'translate(40%, -30%)'
    }
  },
  '@keyframes fadeInDown': {
    '0%': {
      opacity: 0,
      transform: 'translateY(-6px) scale(0.98)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0) scale(1)'
    }
  },
  '@keyframes fadeInRight': {
    '0%': {
      opacity: 0,
      transform: 'translateX(-8px) scale(0.98)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateX(0) scale(1)'
    }
  }
}));

const buildAppPath = (path, query) => {
  if (path.startsWith(APP_ROOT)) {
    return `${path}${query ? `?${query}` : ''}`;
  }
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${APP_ROOT}${normalizedPath}${query ? `?${query}` : ''}`;
};

const TopBar = ({ className, onMobileNavOpen, ...rest }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [manageMenuAnchor, setManageMenuAnchor] = useState(null);
  const [reportMenuAnchor, setReportMenuAnchor] = useState(null);
  const [menuResponsive, setMenuResponsive] = useState(false);
  const [openManageMobile, setOpenManageMobile] = useState(false);
  const [openStatistics, setOpenStatistics] = useState(false);
  const [openDeviceManage, setOpenDeviceManage] = useState(false);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [defaultAlertDevices, setDefaultAlertDevices] = useState([]);
  const [hasAutoOpenedAlertModal, setHasAutoOpenedAlertModal] = useState(false);

  const manageMenuCloseTimeoutRef = useRef(null);
  const reportMenuCloseTimeoutRef = useRef(null);

  const dataLogin = useSelector(state => state.authSlice.dataLogin);
  const isAdmin = useSelector(state => state.authSlice.isAdmin);
  const isAgency = useSelector(state => state.authSlice.isAgency);
  const isUser = useSelector(state => state.authSlice.isUser);
  const isStaff = useSelector(state => state.authSlice.isStaff);
  const isAccountant = useSelector(state => state.authSlice.isAccountant);
  const isSale = useSelector(state => state.authSlice.isSale);
  const isTechnician = useSelector(state => state.authSlice.isTechnician);
  const isDriver = useSelector(state => state.authSlice.isDriver);
  const agencyLevel = Number(useSelector(state => state.authSlice.agencyLevel));

  const listMenu = useMemo(
    () =>
      isAdmin
        ? MENU
        : isAccountant || isSale || isTechnician
          ? []
          : MENU.filter(item => item.value !== 'device' && item.value !== 'agency'),
    [isAdmin, isAccountant, isSale, isTechnician]
  );

  const canShowDropdownMenus = !isAccountant && !isSale && !isTechnician;
  const shouldShowAgencyItem = (isAdmin || isAgency) && agencyLevel !== 2;
  const currentPath = location.pathname;
  const alertVehicleCount = defaultAlertDevices.length;
  const alertDismissStorageKey = dataLogin?.id
    ? `alert-transmission-dismissed:${dataLogin.id}`
    : null;

  const isPathActive = path => currentPath.startsWith(buildAppPath(path));
  const isPathExact = path => currentPath === buildAppPath(path);
  const isManageActive = isPathActive('/manage');
  const isReportActive =
    isPathActive('/baocao-dn') || isPathActive('/baocao-bgt');
  const isStatisticsActive = isPathActive('/manage/statistics');
  const isDevicePathActive = DEVICE_MANAGE_PATHS.some(path =>
    isPathActive(path)
  );

  const fetchListDevice = useCallback(
    async params => {
      const response = await axiosInterceptor.get(
        `/devices?${_convertObjectToQuery({
          page: 1,
          page_size: 1000,
          device_type_id: 2,
          status: 'active',
          ...params
        })}`
      );

      return response.data;
    },
    []
  );

  const refreshAlertDevices = useCallback(async () => {
    if (!isUser) {
      setDefaultAlertDevices([]);
      return;
    }

    const response = await fetchListDevice();
    const devices = response?.payload?.devices || [];

    setDefaultAlertDevices(
      devices.filter(device => Number(device.status_transmission) === 0)
    );
  }, [fetchListDevice, isUser]);

  useEffect(() => {
    setOpenManageMobile(isManageActive);
  }, [isManageActive]);

  useEffect(() => {
    setOpenStatistics(isStatisticsActive);
  }, [isStatisticsActive]);

  useEffect(() => {
    setOpenDeviceManage(isDevicePathActive);
  }, [isDevicePathActive]);

  useEffect(() => {
    clearHoverMenuCloseTimeout(manageMenuCloseTimeoutRef);
    clearHoverMenuCloseTimeout(reportMenuCloseTimeoutRef);
    setManageMenuAnchor(null);
    setReportMenuAnchor(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!isUser) {
      setDefaultAlertDevices([]);
      return;
    }

    let isMounted = true;

    const loadDefaultAlertDevices = async () => {
      try {
        if (isMounted) {
          await refreshAlertDevices();
        }
      } catch (error) {
        if (isMounted) {
          setDefaultAlertDevices([]);
        }
      }
    };

    loadDefaultAlertDevices();

    return () => {
      isMounted = false;
    };
  }, [dataLogin, isUser, refreshAlertDevices]);

  useEffect(() => {
    setHasAutoOpenedAlertModal(false);
  }, [dataLogin]);

  useEffect(() => {
    if (!isUser || hasAutoOpenedAlertModal || defaultAlertDevices.length === 0) {
      return;
    }

    if (
      alertDismissStorageKey &&
      localStorage.getItem(alertDismissStorageKey) === 'true'
    ) {
      setHasAutoOpenedAlertModal(true);
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsAlertModalOpen(true);
      setHasAutoOpenedAlertModal(true);
    }, 1200);

    return () => clearTimeout(timeoutId);
  }, [
    alertDismissStorageKey,
    defaultAlertDevices,
    hasAutoOpenedAlertModal,
    isUser
  ]);

  useEffect(
    () => () => {
      if (manageMenuCloseTimeoutRef.current) {
        clearTimeout(manageMenuCloseTimeoutRef.current);
      }
      if (reportMenuCloseTimeoutRef.current) {
        clearTimeout(reportMenuCloseTimeoutRef.current);
      }
    },
    []
  );

  const manageMenuItems = useMemo(() => {
    if (isAdmin) {
      return [
        { label: 'Thống kê', route: '/manage/statistics/warehouse-transfer' },
        { label: 'Quản trị nhân viên', route: '/manage/service-staffs' },
        { label: 'Quản trị khách hàng', route: '/manage/users' },
        { label: 'Quản trị đại lý', route: '/manage/agencies' },
        { label: 'Quản trị thiết bị', route: '/manage/devices' },
        { label: 'Quản trị quảng cáo', route: '/manage/broadcasts-schedules' },
        { label: 'Phản hồi khách hàng', route: '/manage/feedbacks' },
        { label: 'Quản trị kinh doanh', route: '/manage/sales-orders' },
        { label: 'Nhật ký hoạt động', route: '/manage/action-logs' }
      ];
    }

    if (isAgency) {
      return [
        { label: 'Thống kê', route: '/manage/statistics/warehouse-transfer' },
        { label: 'Quản trị thiết bị', route: '/manage/devices' },
        { label: 'Quản trị khách hàng', route: '/manage/customers' },
        ...(shouldShowAgencyItem
          ? [{ label: 'Quản trị đại lý', route: '/manage/agencies' }]
          : []),
        { label: 'Quản trị nhân viên', route: '/manage/service-staffs' },
        { label: 'Quản trị lái xe', route: '/manage/driver' },
        { label: 'Nhật ký hoạt động', route: '/manage/action-logs' }
      ];
    }

    if (isStaff || isTechnician) {
      return [{ label: 'Quản trị thiết bị', route: '/manage/devices' }];
    }

    if (isAccountant || isSale) {
      return [
        { label: 'Quản trị kinh doanh', route: '/manage/sales-orders' },
        { label: 'Quản trị đại lý', route: '/manage/agencies' },
        { label: 'Quản trị thiết bị', route: '/manage/devices' },
        { label: 'Quản trị nhân viên', route: '/manage/service-staffs' }
      ];
    }

    if (isUser) {
      return [
        { label: 'Quản trị thiết bị', route: '/manage/devices' },
        { label: 'Quản trị lái xe', route: '/manage/driver' },
        { label: 'Quản trị tài khoản lái xe', route: '/manage/accounts-driver' },
        { label: 'Quản trị vùng an toàn', route: '/manage/safe-zone' }
      ];
    }

    if (isDriver) {
      return [{ label: 'Quản trị thiết bị', route: '/manage/devices' }];
    }

    return [];
  }, [
    isAdmin,
    isAgency,
    isStaff,
    isTechnician,
    isAccountant,
    isSale,
    isUser,
    isDriver,
    shouldShowAgencyItem
  ]);

  const mobileStatisticsItems = useMemo(
    () => [
      {
        label: 'Thống kê chuyển kho',
        route: '/manage/statistics/warehouse-transfer'
      },
      {
        label: 'Thống kê dịch vụ',
        route: '/manage/statistics/service'
      }
    ],
    []
  );

  const mobileAdminItems = useMemo(
    () => [
      { label: 'Quản trị nhân viên', route: '/manage/service-staffs' },
      { label: 'Quản trị khách hàng', route: '/manage/users' },
      { label: 'Quản trị đại lý', route: '/manage/agencies' },
      { label: 'Quản trị kinh doanh', route: '/manage/sales-orders' }
    ],
    []
  );

  const mobileDeviceItems = useMemo(
    () => [
      { label: 'Quản lý gói dịch vụ', route: '/manage/service-packages' },
      { label: 'Quản lý loại phương tiện', route: '/manage/vehicle-types' },
      { label: 'Quản lý thiết bị', route: '/manage/devices' },
      { label: 'Quản lý phiên bản thiết bị', route: '/manage/versions' },
      { label: 'Quản lý SIM', route: '/manage/sim' }
    ],
    []
  );

  const toggleDrawer = open => event => {
    if (
      event &&
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }

    setMenuResponsive(open);
  };

  const openMenu = setter => event => {
    setter(event.currentTarget);
  };

  const closeMenu = setter => () => {
    setter(null);
  };

  const clearHoverMenuCloseTimeout = timeoutRef => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const openHoverMenu = (
    setter,
    timeoutRef,
    otherSetter,
    otherTimeoutRef
  ) => event => {
    clearHoverMenuCloseTimeout(timeoutRef);
    if (otherTimeoutRef) {
      clearHoverMenuCloseTimeout(otherTimeoutRef);
    }
    if (otherSetter) {
      otherSetter(null);
    }
    setter(event.currentTarget);
  };

  const scheduleHoverMenuClose = (setter, timeoutRef) => () => {
    clearHoverMenuCloseTimeout(timeoutRef);
    timeoutRef.current = setTimeout(() => {
      setter(null);
      timeoutRef.current = null;
    }, HOVER_MENU_CLOSE_DELAY);
  };

  const closeManageMenu = () => {
    clearHoverMenuCloseTimeout(manageMenuCloseTimeoutRef);
    setManageMenuAnchor(null);
  };

  const closeReportMenu = () => {
    clearHoverMenuCloseTimeout(reportMenuCloseTimeoutRef);
    setReportMenuAnchor(null);
  };

  const switchRoute = (path, query) => {
    navigate(buildAppPath(path, query));
  };

  const go = path => {
    switchRoute(path);
    setMenuResponsive(false);
    setOpenManageMobile(false);
  };

  const handleDesktopMenuItemClick = route => {
    closeManageMenu();
    closeReportMenu();
    switchRoute(route);
  };

  const handleOpenAlertModal = () => {
    setIsAlertModalOpen(true);
    setHasAutoOpenedAlertModal(true);
  };

  const handleCloseAlertModal = () => {
    setIsAlertModalOpen(false);
  };

  const handleLogOut = () => {
    dispatch(logOutAction());
    dispatch(resetListVehicle());
    setProfileMenuAnchor(null);
    navigate('/login');
  };

  const goProfile = () => {
    navigate('/app/account');
    setProfileMenuAnchor(null);
  };

  const avatarImage =
    dataLogin && dataLogin.avatar
      ? `${process.env.REACT_APP_IMG_SERVER}/${dataLogin.avatar}`
      : '/static/images/no-avatar.svg';

  const renderMobileMenuItem = (item, paddingLeft = 32, exact = false) => (
    <ListItem
      key={item.route}
      button
      style={{ paddingLeft }}
      onClick={() => go(item.route)}
    >
      <ListItemText
        primary={item.label}
        primaryTypographyProps={{
          style: {
            color: (exact ? isPathExact(item.route) : isPathActive(item.route))
              ? '#D9042F'
              : paddingLeft > 32
                ? '#6b7280'
                : '#475461',
            fontWeight: (exact ? isPathExact(item.route) : isPathActive(item.route))
              ? 600
              : 400
          }
        }}
      />
    </ListItem>
  );

  return (
    <AppBar className={clsx(classes.root, className)} elevation={0} {...rest}>
      <Toolbar>
        <RouterLink to="/" className={classes.wrapLogo}>
          <Logo />
        </RouterLink>

        <TopBarDesktopNavigation
          classes={classes}
          listMenu={listMenu}
          canShowDropdownMenus={canShowDropdownMenus}
          isPathActive={isPathActive}
          isManageActive={isManageActive}
          isReportActive={isReportActive}
          switchRoute={switchRoute}
          manageMenuAnchor={manageMenuAnchor}
          reportMenuAnchor={reportMenuAnchor}
          onManageMouseEnter={openHoverMenu(
            setManageMenuAnchor,
            manageMenuCloseTimeoutRef,
            setReportMenuAnchor,
            reportMenuCloseTimeoutRef
          )}
          onManageMouseLeave={scheduleHoverMenuClose(
            setManageMenuAnchor,
            manageMenuCloseTimeoutRef
          )}
          onManageMenuMouseEnter={() =>
            clearHoverMenuCloseTimeout(manageMenuCloseTimeoutRef)
          }
          onManageMenuMouseLeave={scheduleHoverMenuClose(
            setManageMenuAnchor,
            manageMenuCloseTimeoutRef
          )}
          onReportMouseEnter={openHoverMenu(
            setReportMenuAnchor,
            reportMenuCloseTimeoutRef,
            setManageMenuAnchor,
            manageMenuCloseTimeoutRef
          )}
          onReportMouseLeave={scheduleHoverMenuClose(
            setReportMenuAnchor,
            reportMenuCloseTimeoutRef
          )}
          onReportMenuMouseEnter={() =>
            clearHoverMenuCloseTimeout(reportMenuCloseTimeoutRef)
          }
          onReportMenuMouseLeave={scheduleHoverMenuClose(
            setReportMenuAnchor,
            reportMenuCloseTimeoutRef
          )}
          closeManageMenu={closeManageMenu}
          closeReportMenu={closeReportMenu}
          manageMenuItems={manageMenuItems}
          reportMenuItems={itemsReport}
          handleDesktopMenuItemClick={handleDesktopMenuItemClick}
        />

        <TopBarUserActions
          avatarImage={avatarImage}
          languageMenuAnchor={languageMenuAnchor}
          profileMenuAnchor={profileMenuAnchor}
          openLanguageMenu={openMenu(setLanguageMenuAnchor)}
          closeLanguageMenu={closeMenu(setLanguageMenuAnchor)}
          openProfileMenu={openMenu(setProfileMenuAnchor)}
          closeProfileMenu={closeMenu(setProfileMenuAnchor)}
          goProfile={goProfile}
          handleLogOut={handleLogOut}
          onOpenMobileMenu={event => {
            if (typeof onMobileNavOpen === 'function') {
              onMobileNavOpen(event);
            }
            toggleDrawer(true)(event);
          }}
          showAlertButton={isUser}
          alertVehicleCount={alertVehicleCount}
          alertBadgeClassName={classes.alertBadge}
          onOpenAlertModal={handleOpenAlertModal}
        />

        <TopBarMobileDrawer
          menuResponsive={menuResponsive}
          toggleDrawer={toggleDrawer}
          listMenu={listMenu}
          switchRoute={switchRoute}
          setMenuResponsive={setMenuResponsive}
          canShowDropdownMenus={canShowDropdownMenus}
          openManageMobile={openManageMobile}
          setOpenManageMobile={setOpenManageMobile}
          isManageActive={isManageActive}
          isAccountant={isAccountant}
          isSale={isSale}
          renderMobileMenuItem={renderMobileMenuItem}
          isAdmin={isAdmin}
          openStatistics={openStatistics}
          setOpenStatistics={setOpenStatistics}
          isStatisticsActive={isStatisticsActive}
          mobileStatisticsItems={mobileStatisticsItems}
          mobileAdminItems={mobileAdminItems}
          isAgency={isAgency}
          isStaff={isStaff}
          isUser={isUser}
          isTechnician={isTechnician}
          openDeviceManage={openDeviceManage}
          setOpenDeviceManage={setOpenDeviceManage}
          isDevicePathActive={isDevicePathActive}
          mobileDeviceItems={mobileDeviceItems}
          isPathActive={isPathActive}
        />

        <AlertTransmissionModal
          open={isAlertModalOpen}
          onClose={handleCloseAlertModal}
          defaultDevices={defaultAlertDevices}
          dismissStorageKey={alertDismissStorageKey}
          getListDevice={fetchListDevice}
          onTransmissionUpdated={refreshAlertDevices}
        />
      </Toolbar>
    </AppBar>
  );
};

TopBar.propTypes = {
  className: PropTypes.string,
  onMobileNavOpen: PropTypes.func
};

export default TopBar;
